import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2 } from "lucide-react";
import SocialShareButtons from "@/components/SocialShareButtons";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RegistrationPromptProps {
  agentConfig: any;
  sessionId?: string;
  agentId?: string;
  onComplete: () => void;
}

const RegistrationPrompt = ({ agentConfig, sessionId, agentId, onComplete }: RegistrationPromptProps) => {
  const [isSigningUp, setIsSigningUp] = useState(false);

  const handleSignup = async () => {
    if (isSigningUp) return;
    setIsSigningUp(true);

    try {
      // If sessionId already exists (some flows set it earlier), reuse it.
      // Otherwise create an intro agent on Call24x7 and get the real session_id/agent_id.
      let sid = sessionId;
      let aid = agentId;

      if (!sid || !aid) {
        const { data, error } = await supabase.functions.invoke('intro-agent', {
          body: { agentConfig },
        });

        if (error || !data?.success) {
          console.error('Error creating intro agent:', error || data);
          toast.error('Failed to prepare your agent. Please try again.');
          setIsSigningUp(false);
          return;
        }

        sid = data.sessionId as string;
        aid = data.agentId as string;
      }

      // Set cookie (works across subdomains only when hosted on call24x7.ai)
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + (24 * 60 * 60 * 1000)); // 24 hours
      const cookieDomain = window.location.hostname.endsWith('call24x7.ai') ? '; domain=.call24x7.ai' : '';
      document.cookie = `session_id=${sid}${cookieDomain}; expires=${expirationDate.toUTCString()}; path=/; secure; SameSite=None`;

      // Redirect to signup page (include session_id so it also works from preview domains)
      const signupUrl = new URL('https://manage.call24x7.ai/signup');
      signupUrl.searchParams.set('session_id', sid);
      if (aid) signupUrl.searchParams.set('agent_id', aid);
      window.location.href = signupUrl.toString();
    } catch (err) {
      console.error('Signup error:', err);
      toast.error('Something went wrong. Please try again.');
      setIsSigningUp(false);
    }
  };

  return (
    <div className="max-w-md mx-auto animate-fade-in">
      <Card className="bg-gray-900/80 backdrop-blur-sm border-gray-700/50 shadow-2xl p-8">
        <div className="text-center space-y-6">
          <div className="inline-block p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl">
            <Sparkles className="h-12 w-12 text-purple-400" />
          </div>
          
          <h2 className="text-3xl font-bold text-white">
            Save Your Agent & Get Free Credits!
          </h2>
          
          <p className="text-gray-300">
            Create an account to save your AI phone agent and receive 1 hour of free inbound and outbound calls
          </p>

          <div className="space-y-3 pt-4">
            <div className="flex items-center gap-3 text-left">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <p className="text-sm text-gray-300">Save and manage your custom agent</p>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
              <p className="text-sm text-gray-300">1 hour of free phone calls included</p>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="w-2 h-2 rounded-full bg-pink-500"></div>
              <p className="text-sm text-gray-300">Continue editing your agent anytime</p>
            </div>
          </div>

          <Button
            onClick={handleSignup}
            disabled={isSigningUp}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg rounded-xl mt-8"
          >
            {isSigningUp ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Saving agent...
              </>
            ) : (
              'Sign Up & Save Agent'
            )}
          </Button>

          {/* Social Share Buttons */}
          <div className="pt-4 border-t border-gray-700/50">
            <SocialShareButtons 
              title={`I just created "${agentConfig?.name || 'my AI phone agent'}" with Call24x7.AI!`}
              text="Check out this amazing AI phone agent builder - create your own in 60 seconds!"
            />
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Session ID: {sessionId}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default RegistrationPrompt;
